import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';
import {
  SITE_VISITS,
  THREAD_STATUSES,
} from './shared-responder-v2-core-thread.consts';
import type {
  QuestionSpec,
  ResponderThread,
} from './shared-responder-v2-core-thread.types';

/**
 * Whether this question is one the responder must open a site for at all.
 * `ownsContentUrl` is the shared capability table's own answer to "which kinds
 * carry a site" — Task and Url — so the gate cannot drift from the kinds the
 * builder authors a URL for. The site is the one the card actually offers (the
 * question's own or the one carried from an earlier question); with none there
 * is nothing to open, however the question was authored.
 */
export function isSiteVisitRequired(
  spec: QuestionSpec,
  effectiveSiteUrl: string | null
): boolean {
  return (
    getThreadBehaviour(spec.kind).ownsContentUrl && effectiveSiteUrl !== null
  );
}

/**
 * Whether the responder may not advance past this question yet, because product
 * requires the site to be opened at least once first.
 *
 * Blocking is the narrow case and every uncertainty falls out of it: only an
 * explicit `unopened` on a question that genuinely has a site to open holds the
 * responder, so a missing spec, a missing thread, a kind with no site, a
 * question with no site to offer, a visit the embed reported `unavailable` and a
 * thread rehydrated without the field all leave Continue exactly where it was.
 */
export function isSiteGateBlocking(
  spec: QuestionSpec | null,
  thread: ResponderThread | null,
  effectiveSiteUrl: string | null
): boolean {
  if (spec === null || thread === null) {
    return false;
  }

  return (
    isSiteVisitRequired(spec, effectiveSiteUrl) &&
    thread.siteVisit === SITE_VISITS.Unopened
  );
}

/**
 * Whether the site gate is what is holding this thread back — the predicate
 * every advance-affordance (kernel and UI alike) must agree on.
 *
 * Scoped to a thread the backend has not closed: a thread rehydrated as
 * `Closed` on resume is a question the backend already considers done, and
 * `siteVisit` is session-local — reset to `Unopened` on every reload — so
 * re-running the raw `isSiteGateBlocking` on it would trap a resumed responder
 * on a task they already finished, with no composer to answer from and
 * Continue hidden.
 */
export function isBlockedBySiteGate(
  spec: QuestionSpec | null,
  thread: ResponderThread | null,
  effectiveSiteUrl: string | null
): boolean {
  return (
    thread !== null &&
    thread.status !== THREAD_STATUSES.Closed &&
    isSiteGateBlocking(spec, thread, effectiveSiteUrl)
  );
}

/**
 * Which site a Task question's Open-site card should offer: its own
 * `contentUrl` when it has one, otherwise the last site opened earlier this
 * session, otherwise the page the host app is embedded in when there is one,
 * otherwise nothing at all — Jaala's carry-forward rule with the host page as
 * its last resort. `''` counts as unset: the builder writes it when the URL
 * card is open but nothing has been typed into it yet, which is not a URL to
 * open.
 */
export function resolveTaskSiteUrl(
  ownUrl: string | null,
  carriedUrl: string | null,
  hostPageUrl: string | null = null
): string | null {
  if (ownUrl !== null && ownUrl !== '') {
    return ownUrl;
  }

  return carriedUrl ?? hostPageUrl;
}

export function isSiteVisitInProgress(thread: ResponderThread): boolean {
  return (
    thread.siteVisit === SITE_VISITS.Opened &&
    thread.answer === null &&
    thread.status !== THREAD_STATUSES.Closed
  );
}

export function isHostPageSite(
  effectiveSiteUrl: string | null,
  hostPageUrl: string | null
): boolean {
  return effectiveSiteUrl !== null && effectiveSiteUrl === hostPageUrl;
}
